import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { mapFeatureNameToIcon } from '@hotelplan/libs.mappers';
import { sx2CssThemeFn, sx2CssTheme } from '@hotelplan/util.theme.sxc';
import { useCommonFeatureGroups } from 'fdr/components/candidate/fdr-wishlist/fdr-use-common-feature-groups';
import { TFdrWishlistCompareItem } from './fdr-wishlist-compare-items.types';
import { getFeatureIconAndColorForCompareItem } from './fdr-wishlist.utils';

interface IWishlistCompareItemFeaturesMobile {
  items: (TFdrWishlistCompareItem | null)[];
  className?: string;
}

const FeatureGroupWrapper = styled.div({
  display: 'flex',
});

const FeatureGroupName = styled.span.attrs({
  className: 'featureGroupName',
})(({ theme, theme: { space } }) => ({
  fontWeight: 'bold',
  '.wishlist-feature-mobile-icon': {
    ...theme.icons.lg,
    lineHeight: '1',
    marginRight: space[3],
  },
}));

const FeaturesList = styled.ul(({ theme: { space } }) => ({
  padding: `${space[3]} ${space[4]}`,
}));

const FeatureItem = styled.li(
  sx2CssThemeFn({
    display: 'flex',
    '&:not(:last-child)': {
      mb: 2,
    },
    justifyContent: 'space-between',
  })
);

const FeatureIconWrapper = styled.span<{ color: string; isVisible: boolean }>(
  ({ color, isVisible, theme, theme: { space, colors } }) => ({
    display: 'flex',
    marginBottom: space[2],
    '.wishlist-feature-first-title': sx2CssTheme(theme, {
      mr: 1,
      ml: -4,
      fontSize: 1,
    }),
    '.wishlist-feature-second-title': sx2CssTheme(theme, {
      ml: 1,
      mr: -4,
      fontSize: 1,
    }),
    '.wishlist-feature-icon': {
      ...theme.icons.lg,
      color: color === 'error' ? colors.error : colors.success,
      visibility: isVisible ? 'visible' : 'hidden',
    },
  })
);

const FeatureName = styled.span.attrs({
  className: 'featureName',
})({
  textAlign: 'center',
});

const FdrWishlistCompareItemFeaturesMobile: React.FC<IWishlistCompareItemFeaturesMobile> =
  ({ items, className }) => {
    const [t] = useTranslation(['wishlist']);
    const commonFeatureGroups = useCommonFeatureGroups();
    return (
      <>
        {commonFeatureGroups.map(({ id, name, features }) => {
          return (
            <FeatureGroupWrapper
              key={`feature-group-${id}`}
              className={className}
            >
              <FeatureGroupName>
                <BsIcon
                  name={mapFeatureNameToIcon(id)}
                  className="wishlist-feature-mobile-icon"
                />
                {name}
              </FeatureGroupName>
              <FeaturesList>
                {features.map(feature => {
                  const firstProductFeatureIcon =
                    items[0].descriptionFeatureGroups
                      .find(fg => fg.id === id)
                      ?.features.find(f => f.id === feature.id)?.icon ||
                    getFeatureIconAndColorForCompareItem(feature, items[0]);

                  const secondProductFeatureIcon =
                    items[1]?.descriptionFeatureGroups
                      ?.find(fg => fg.id === id)
                      ?.features.find(f => f.id === feature.id)?.icon ||
                    getFeatureIconAndColorForCompareItem(feature, items[1]);

                  if (!firstProductFeatureIcon || !secondProductFeatureIcon) {
                    return null;
                  }

                  const {
                    color: firstOfferColor,
                    name: firstOfferIcon,
                    isNA: isFirstOfferNA,
                    isVisible: isFirstOfferVisible,
                  } = firstProductFeatureIcon;
                  const {
                    color: secondOfferColor,
                    name: secondOfferIcon,
                    isNA: isSecondOfferNA,
                    isVisible: isSecondOfferVisible,
                  } = secondProductFeatureIcon;

                  return (
                    <FeatureItem key={`feature-${feature.id}`}>
                      <FeatureIconWrapper
                        isVisible={isFirstOfferVisible}
                        color={firstOfferColor}
                      >
                        {isFirstOfferNA && (
                          <span className="wishlist-feature-first-title">
                            {t('wishlist:featureNA.title')}
                          </span>
                        )}
                        <BsIcon
                          name={firstOfferIcon}
                          className="wishlist-feature-icon"
                        />
                      </FeatureIconWrapper>
                      <FeatureName>{feature.name}</FeatureName>
                      <FeatureIconWrapper
                        isVisible={isSecondOfferVisible}
                        color={secondOfferColor}
                      >
                        <BsIcon
                          name={secondOfferIcon}
                          className="wishlist-feature-icon"
                        />
                        {isSecondOfferNA && (
                          <span className="wishlist-feature-second-title">
                            {t('wishlist:featureNA.title')}
                          </span>
                        )}
                      </FeatureIconWrapper>
                    </FeatureItem>
                  );
                })}
              </FeaturesList>
            </FeatureGroupWrapper>
          );
        })}
      </>
    );
  };

export default FdrWishlistCompareItemFeaturesMobile;
